import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, DatePicker, Select, Button, Space, Card,
    Typography, message, Row, Col, Divider, Tooltip
} from 'antd';
import { useAuth } from '../../context/AuthContext';
import {
    SearchOutlined, DownloadOutlined,
    BarChartOutlined, ClockCircleOutlined, UserOutlined, PieChartOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { reportService, pbxReportCatalogService, breakService } from '../../services/api';
import { BarChart, Bar, PieChart, Pie, XAxis, YAxis, Tooltip as RechartsTooltip, CartesianGrid, Legend, Cell, ResponsiveContainer } from 'recharts';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

const ReportBreaks = () => {
    const auth = useAuth() || {};
    const permissions = Array.isArray(auth.permissions) ? auth.permissions : [];
    const canExport = permissions.includes('callcenter.reports.breaks.export') || permissions.includes('callcenter.reports.export');

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [kpis, setKpis] = useState({});
    const [chartsData, setChartsData] = useState([]);
    const [total, setTotal] = useState(0);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15 });

    const [filters, setFilters] = useState({
        date_range: [dayjs().startOf('day'), dayjs().endOf('day')],
        users: [],
        breakTypes: []
    });

    const [users, setUsers] = useState([]);
    const [breakTypes, setBreakTypes] = useState([]);

    const fetchCatalogs = async () => {
        try {
            const [userRes, breakRes] = await Promise.all([
                pbxReportCatalogService.getUsersWithExtension('agent'),
                breakService.list({ per_page: 100 }) // Load all active breaks
            ]);
            if (userRes.status === 'success') setUsers(userRes.data);
            if (breakRes.status === 'success' || breakRes.data) {
                // Break list response might be slightly different depending on pagination wrapper
                const breaksData = Array.isArray(breakRes.data) ? breakRes.data : (breakRes.data.data || []);
                setBreakTypes(breaksData);
            }
        } catch (error) {
            console.error("Error fetching catalogs", error);
        }
    };

    const fetchData = useCallback(async (page = 1, pageSize = 15) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: pageSize,
                users: filters.users.join(','),
                breakTypes: filters.breakTypes.join(','),
                date_range: filters.date_range ? filters.date_range.map(d => d.format('YYYY-MM-DD')).join(',') : null
            };

            const response = await reportService.getBreaksReport(params);
            if (response.status === 'success') {
                setData(response.data.items);
                setTotal(response.data.total);
                setKpis(response.data.kpis);
                setChartsData(response.data.charts?.break_distribution || []);
                setPagination({ current: page, pageSize });
            }
        } catch (error) {
            message.error("Error al cargar los datos del reporte");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchCatalogs();
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTableChange = (pag) => {
        fetchData(pag.current, pag.pageSize);
    };

    const handleExport = () => {
        const params = {
            users: filters.users.join(','),
            breakTypes: filters.breakTypes.join(','),
            date_range: filters.date_range ? filters.date_range.map(d => d.format('YYYY-MM-DD')).join(',') : null
        };
        const url = reportService.getBreaksReportExportUrl(params);
        window.open(url, '_blank');
    };

    const formatSeconds = (s) => {
        if (s === null || s === undefined) return '0:00:00';
        const roundedS = Math.round(Number(s));
        const hrs = Math.floor(roundedS / 3600);
        const min = Math.floor((roundedS % 3600) / 60);
        const sec = roundedS % 60;
        return `${hrs}:${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    };

    const columns = [
        {
            title: 'Agente',
            dataIndex: 'agent_name',
            key: 'agent_name',
        },
        {
            title: 'Tipo de Break',
            dataIndex: 'break_name',
            key: 'break_name',
        },
        {
            title: 'Frecuencia (Veces)',
            dataIndex: 'break_count',
            key: 'break_count',
            align: 'center'
        },
        {
            title: (
                <Space size="small">
                    Total de Horas
                    <Tooltip title="Suma absoluta del tiempo invertido en este Break convertido a horas.">
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                </Space>
            ),
            dataIndex: 'total_duration_hours',
            key: 'total_duration_hours',
            align: 'right',
            render: (val) => `${val}h`
        },
        {
            title: (
                <Space size="small">
                    Promedio Duración
                    <Tooltip title="Tiempo habitual que el agente demora cada vez que activa este Break. (Formato H:m:s)">
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                </Space>
            ),
            dataIndex: 'avg_duration_seconds',
            key: 'avg_duration_seconds',
            align: 'right',
            render: (val) => formatSeconds(val)
        },
        {
            title: (
                <Space size="small">
                    Impacto Disp. (%)
                    <Tooltip title="Porcentaje de inactividad que este Break representa frente a una jornada teórica de 8 horas (28800 segundos). Si es mayor a 15% indica alto riesgo (rojo).">
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                </Space>
            ),
            dataIndex: 'availability_impact',
            key: 'availability_impact',
            align: 'right',
            render: (val) => {
                let color = val > 15 ? '#cf1322' : (val > 10 ? '#d48806' : '#389e0d');
                return <span style={{ color, fontWeight: 'bold' }}>{val}%</span>;
            }
        },
    ];

    return (
        <div className="report-breaks fade-in">
            <Card bordered={false} className="report-card" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: 24 }}>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                        <RangePicker
                            value={filters.date_range}
                            onChange={(val) => setFilters({ ...filters, date_range: val })}
                            placeholder={['Inicio', 'Fin']}
                            style={{ width: 280 }}
                        />

                        <Select
                            mode="multiple"
                            placeholder="Agentes"
                            value={filters.users}
                            onChange={(val) => setFilters({ ...filters, users: val })}
                            style={{ minWidth: 200, maxWidth: 350 }}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            maxTagCount="responsive"
                        >
                            {users.map(u => <Option key={u.id} value={u.id}>{u.name}</Option>)}
                        </Select>

                        <Select
                            mode="multiple"
                            placeholder="Tipos de Break"
                            value={filters.breakTypes}
                            onChange={(val) => setFilters({ ...filters, breakTypes: val })}
                            style={{ minWidth: 200, maxWidth: 350 }}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            maxTagCount="responsive"
                        >
                            {breakTypes.map(b => <Option key={b.id} value={b.id}>{b.name}</Option>)}
                        </Select>

                        <Button type="primary" icon={<SearchOutlined />} onClick={() => fetchData(1, pagination.pageSize)} loading={loading}>
                            Calcular
                        </Button>
                        {canExport && (
                            <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={data.length === 0}>
                                Exportar
                            </Button>
                        )}
                    </div>
                </Space>
            </Card>

            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                    <Card style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <Title level={3} style={{ margin: 0, color: '#0050b3' }}>
                                    {kpis.total_hours || 0} <span style={{ fontSize: 16 }}>Hrs</span>
                                </Title>
                                <Text type="secondary">
                                    Total Horas Invertidas
                                    <Tooltip title="El total global de inactividad (Shrinkage) del equipo. A mayor suma, menos disponibilidad general para las campañas.">
                                        <InfoCircleOutlined style={{ marginLeft: 6, cursor: 'help' }} />
                                    </Tooltip>
                                </Text>
                            </div>
                            <ClockCircleOutlined style={{ fontSize: 32, color: '#1890ff', opacity: 0.5 }} />
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <Title level={4} style={{ margin: 0, color: '#ad6800' }}>
                                    {kpis.top_break_name || 'N/A'}
                                </Title>
                                <Text type="secondary">
                                    Break Más Frecuente ({kpis.top_break_hours || 0} Hrs)
                                    <Tooltip title="El estado de inactividad que está generando la mayor pérdida de tiempo acumulada a nivel global grupal.">
                                        <InfoCircleOutlined style={{ marginLeft: 6, cursor: 'help' }} />
                                    </Tooltip>
                                </Text>
                            </div>
                            <PieChartOutlined style={{ fontSize: 32, color: '#faad14', opacity: 0.5 }} />
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card style={{ backgroundColor: '#fff1f0', border: '1px solid #ffa39e' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <Title level={4} style={{ margin: 0, color: '#a8071a' }}>
                                    {kpis.top_agent_name || 'N/A'}
                                </Title>
                                <Text type="secondary">
                                    Agente con Mayor Fuga ({kpis.top_agent_hours || 0} Hrs)
                                    <Tooltip title="El operador individual (Outlier) que ha sumado la mayor inactividad en todo el periodo filtrado.">
                                        <InfoCircleOutlined style={{ marginLeft: 6, cursor: 'help' }} />
                                    </Tooltip>
                                </Text>
                            </div>
                            <UserOutlined style={{ fontSize: 32, color: '#f5222d', opacity: 0.5 }} />
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <Space align="baseline">
                                <><BarChartOutlined /> Tiempo por Tipo (Horas)</>
                                <Tooltip title="Suma total de inactividad agrupada por Tipo de Break expresada en horas absolutas. Permite identificar de un vistazo los motivos que más pesan nominalmente en la operación.">
                                    <InfoCircleOutlined style={{ color: '#bfbfbf', fontWeight: 'normal', fontSize: '14px' }} />
                                </Tooltip>
                            </Space>
                        }
                        style={{ height: '100%' }}
                    >
                        <div style={{ width: '100%', height: 300 }}>
                            {chartsData.length > 0 ? (
                                <ResponsiveContainer>
                                    <BarChart data={chartsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="break_name" />
                                        <YAxis />
                                        <RechartsTooltip />
                                        <Bar dataKey="total_duration_hours" fill="#1890ff" name="Horas" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <Text type="secondary">Sin datos para graficar</Text>
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <Space align="baseline">
                                <><PieChartOutlined /> Distribución Porcentual</>
                                <Tooltip title="Muestra cómo se reparte el 'pastel' (100%) del tiempo inactivo total filtrado. Ayuda a visualizar qué proporción representa un motivo particular respecto al tiempo inactivo total.">
                                    <InfoCircleOutlined style={{ color: '#bfbfbf', fontWeight: 'normal', fontSize: '14px' }} />
                                </Tooltip>
                            </Space>
                        }
                        style={{ height: '100%' }}
                    >
                        <div style={{ width: '100%', height: 300 }}>
                            {chartsData.length > 0 ? (
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={chartsData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                            outerRadius={100}
                                            fill="#8884d8"
                                            dataKey="total_duration_hours"
                                            nameKey="break_name"
                                        >
                                            {chartsData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <Text type="secondary">Sin datos para graficar</Text>
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>

            <Card
                title="Detalle Acumulado por Agente y Break"
                bordered={false}
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            >
                <Table
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    rowKey={(record) => `${record.agent_id}-${record.break_id}`}
                    pagination={{
                        ...pagination,
                        total,
                        showSizeChanger: true,
                        pageSizeOptions: ['15', '30', '50', '100'],
                        showTotal: (total) => `Total: ${total} registros`
                    }}
                    onChange={handleTableChange}
                    size="middle"
                    scroll={{ x: 800 }}
                />
            </Card>
        </div>
    );
};

export default ReportBreaks;
